import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { searchProducts } from '../services/products';
import { getStorageUrl } from '../services/api';
import './SearchBar.css';

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Sync with URL query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('search');
    if (q) {
      setQuery(q);
    }
  }, [location.search]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (query.length >= 2) {
        // Only fetch and show dropdown if the user is actively typing/focused
        if (document.activeElement === inputRef.current) {
            setLoading(true);
            try {
            const data = await searchProducts(query);
            setResults(data);
            setShowResults(true);
            } catch (error) {
            console.error("Search error", error);
            } finally {
            setLoading(false);
            }
        }
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowResults(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const handleSelect = (id) => {
    navigate(`/products/${id}`);
    setShowResults(false);
    // setQuery(''); // Keep the query? Or clear it? User might want to go back.
    // Usually standard search bars keep the query or clear it. 
    // If we navigate to detail, maybe keep it?
    // But if we clear it, the list page might look weird if it was filtered.
    // Let's clear it for now as per previous behavior, but actually...
    // If I click a result, I go to that product. The main list is irrelevant.
    setQuery(''); 
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/products?search=${encodeURIComponent(query)}`);
      setShowResults(false);
      // Blur to hide dropdown if logic depends on focus
      if (inputRef.current) inputRef.current.blur();
    }
  };

  return (
    <section className="search-section">
      <div className="search-container" ref={wrapperRef}>
        <input 
          ref={inputRef}
          type="text" 
          placeholder="Search for products..." 
          className="search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(e)}
          onFocus={() => {
            if (query.length >= 2 && results.length > 0) {
              setShowResults(true);
            }
          }}
        />
        <button className="search-btn" onClick={handleSearchSubmit}>Search</button>

        {showResults && (
         <div className="search-results">
           {loading ? (
             <div className="search-loading">Loading...</div>
           ) : results.length > 0 ? (
             <ul>
               {results.map(product => (
                 <li key={product.id} onClick={() => handleSelect(product.id)}>
                   <div className="search-item-image">
                     {product.image && (
                        <img src={getStorageUrl(product.image)} alt={product.name} />
                     )}
                   </div>
                   <div className="search-item-info">
                     <span className="search-item-title">{product.name}</span>
                     <span className="search-item-subtitle">{product.subtitle}</span>
                   </div>
                 </li>
               ))}
             </ul>
           ) : (
             <div className="search-no-results">No products found</div>
           )}
         </div>
       )}
      </div>
    </section>
  );
};

export default SearchBar;
